// GENERATED by @platform/contracts from specs/engagement/main.tsp. Do not edit; run `pnpm generate`.
/* eslint-disable */
import { z } from 'zod';
/** Tenant identifier. The control plane's own resources use the literal `platform`. */
export const TenantId = z.string().regex(/^[a-z0-9][a-z0-9-]{1,62}$/);
/**
 * `email` and `push` are delivered; `sms` is the Phase 3 seam (carrier registration per tenant).
 * `push` reaches a person's registered phones with a fixed sentence and no record data.
 */
export const NotificationChannel = z.enum(['email', 'sms', 'push']);
/** Whether a subject may be contacted on a channel, and for what. */
export const ConsentStatus = z.enum(['opted-in', 'opted-out']);
/** `marketing` opt-out leaves transactional mail allowed (and recorded); `all` blocks every send. */
export const ConsentScope = z.enum(['marketing', 'all']);
/** How the entry came about. */
export const ConsentSource = z.enum(['manual', 'bounce', 'complaint', 'form', 'import']);
/** The current consent state of one subject (an email address) on one channel, with its history. */
export const ConsentEntry = z.object({
    tenantId: TenantId,
    subject: z.string(),
    channel: NotificationChannel,
    status: ConsentStatus,
    scope: ConsentScope,
    source: ConsentSource,
    note: z.string().optional(),
    recordedBy: z.string(),
    recordedAt: z.iso.datetime({ offset: true }),
});
export const ConsentRecord = z.object({
    subject: z.string(),
    channel: NotificationChannel,
    status: ConsentStatus,
    scope: ConsentScope.default('marketing'),
    note: z.string().max(500).optional(),
});
/** A registered phone's id: derived from its push token, so the same token is the same device. */
export const DeviceId = z.string().regex(/^[a-z2-7]{26}$/);
/** The operating system a registered phone runs; its push token comes from that system. */
export const DevicePlatform = z.enum(['ios', 'android']);
/** `disabled`: the push service reported the token dead; nothing is sent to it until it registers again. */
export const DeviceStatus = z.enum(['active', 'disabled']);
/** A phone the caller registered for push in this tenant. The token itself is never returned. */
export const Device = z.object({
    tenantId: TenantId,
    deviceId: DeviceId,
    platform: DevicePlatform,
    status: DeviceStatus,
    registeredAt: z.iso.datetime({ offset: true }),
    lastRegisteredAt: z.iso.datetime({ offset: true }),
});
/** What the app sends: its platform and the push token its operating system issued. */
export const DeviceRegistration = z.object({
    platform: DevicePlatform,
    token: z
        .string()
        .regex(/^[A-Za-z0-9_:.\-]+$/)
        .min(16)
        .max(1024),
});
/** A notice staff published (ADR in-app-notices-from-staff): `n_` and a random token. */
export const NoticeId = z.string().regex(/^n_[A-Za-z0-9]{12,40}$/);
/**
 * How much a notice asks of the person. `info` and `important` can be dismissed; a `security`
 * notice stays on screen until the person says they have read it.
 */
export const NoticeSeverity = z.enum(['info', 'important', 'security']);
/** An optional link in a notice, to the page that says more. https only. */
export const NoticeLink = z.object({
    label: z.string().min(1).max(60),
    url: z
        .string()
        .regex(/^https:\/\/[^\s]+$/)
        .max(2000),
});
/**
 * A notice from platform staff to the caller (ADR in-app-notices-from-staff): the text, how much
 * it asks of them, and whether they have already acknowledged it. Staff identities are not shown.
 */
export const Notice = z.object({
    noticeId: NoticeId,
    tenantId: TenantId,
    severity: NoticeSeverity,
    title: z.string(),
    body: z.string(),
    link: NoticeLink.optional(),
    publishedAt: z.iso.datetime({ offset: true }),
    expiresAt: z.iso.datetime({ offset: true }).optional(),
    acknowledgedAt: z.iso.datetime({ offset: true }).optional(),
});
/** ULID-style opaque identifier for records. */
export const RecordId = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/);
/** Templates live in engagement's code; a producer names one and supplies its data. */
export const NotificationTemplate = z.enum([
    'invitation',
    'receipt',
    'payment-failed',
    'password-reset-notice',
    'checkout-link',
    'workspace-delayed',
    'account-ready',
    'onboarding-failed-alert',
    'reminder-due',
    'message-received',
    'task-assigned',
    'account-notice',
]);
/**
 * Lifecycle of one message. `queued` → `sent` (provider accepted) → `delivered` | `bounced` |
 * `complained`; `failed` and `suppressed` are terminal without a provider round trip.
 */
export const NotificationStatus = z.enum([
    'queued',
    'sent',
    'delivered',
    'bounced',
    'complained',
    'failed',
    'suppressed',
]);
/** What the tenant's rendering of a message looked like — kept only for sink recipients (`*.invalid`). */
export const RenderedMessage = z.object({ subject: z.string(), text: z.string() });
/** One message engagement was asked to deliver for this tenant. */
export const Notification = z.object({
    notificationId: RecordId,
    tenantId: TenantId,
    channel: NotificationChannel,
    template: NotificationTemplate,
    recipient: z.string(),
    recipientName: z.string().optional(),
    subject: z.string().optional(),
    status: NotificationStatus,
    idempotencyKey: z.string(),
    providerMessageId: z.string().optional(),
    reason: z.string().optional(),
    correlationId: z.string(),
    requestedAt: z.iso.datetime({ offset: true }),
    sentAt: z.iso.datetime({ offset: true }).optional(),
    updatedAt: z.iso.datetime({ offset: true }),
    rendered: RenderedMessage.optional(),
});
/**
 * One person's notification preferences in one tenant. Transactional security mail (sign-in
 * codes, password resets) is never switchable; marketing consent is the consent ledger.
 */
export const NotificationPreferences = z.object({
    tenantId: TenantId,
    push: z.boolean(),
    updatedAt: z.iso.datetime({ offset: true }).optional(),
});
export const PreferencesUpdate = z.object({ push: z.boolean() });
/** Machine-readable error codes. Never say whether a resource exists to a caller outside its tenant. */
export const ErrorCode = z.enum([
    'validation',
    'unauthenticated',
    'forbidden',
    'notFound',
    'conflict',
    'rateLimited',
    'internal',
]);
export const ErrorDetail = z.object({ path: z.string().optional(), message: z.string() });
/** The one error shape. Sent with the matching HTTP status. */
export const ErrorResponse = z.object({
    code: ErrorCode,
    message: z.string(),
    requestId: z.string(),
    details: z.array(ErrorDetail).optional(),
});
/** One validator set per operation, keyed by operationId. Same order as the route manifest. */
export const operations = {
    Consent_list: {
        operationId: 'Consent_list',
        method: 'GET',
        path: '/consent',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
            subject: z.string().optional(),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(ConsentEntry),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Consent_add: {
        operationId: 'Consent_add',
        method: 'POST',
        path: '/consent',
        params: z.object({}),
        query: z.object({}),
        body: ConsentRecord,
        responses: {
            '201': z.object({ tenantId: TenantId, requestId: z.string(), data: ConsentEntry }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Devices_list: {
        operationId: 'Devices_list',
        method: 'GET',
        path: '/devices',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Device),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Devices_register: {
        operationId: 'Devices_register',
        method: 'POST',
        path: '/devices',
        params: z.object({}),
        query: z.object({}),
        body: DeviceRegistration,
        responses: {
            '201': z.object({ tenantId: TenantId, requestId: z.string(), data: Device }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Devices_remove: {
        operationId: 'Devices_remove',
        method: 'DELETE',
        path: '/devices/{deviceId}',
        params: z.object({ deviceId: DeviceId }),
        query: z.object({}),
        responses: {
            '204': z.undefined(),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Notices_list: {
        operationId: 'Notices_list',
        method: 'GET',
        path: '/notices',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Notice),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Notices_acknowledge: {
        operationId: 'Notices_acknowledge',
        method: 'POST',
        path: '/notices/{noticeId}/acknowledge',
        params: z.object({ noticeId: NoticeId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Notice }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Notifications_list: {
        operationId: 'Notifications_list',
        method: 'GET',
        path: '/notifications',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
            since: z.iso.datetime({ offset: true }).optional(),
            status: NotificationStatus.optional(),
            template: NotificationTemplate.optional(),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Notification),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Notifications_get: {
        operationId: 'Notifications_get',
        method: 'GET',
        path: '/notifications/{notificationId}',
        params: z.object({ notificationId: RecordId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Notification }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Preferences_get: {
        operationId: 'Preferences_get',
        method: 'GET',
        path: '/preferences',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: NotificationPreferences }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Preferences_update: {
        operationId: 'Preferences_update',
        method: 'PUT',
        path: '/preferences',
        params: z.object({}),
        query: z.object({}),
        body: PreferencesUpdate,
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: NotificationPreferences }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
};
//# sourceMappingURL=schemas.js.map